import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { updateStep1, updateStep2 } from "../api";
import { ApplicationState } from "../../store";
import {
  ConformityLevel,
  DeclarationOfConformityDto,
} from "../../autogenerated/models";
import { toast } from "react-toastify";
import {
  allFilesUploaded,
  hasUnmetDependencies,
  minExportLevelForProfiles,
  minImportLevelForProfiles,
} from "../../helpers/testSelectionHelpers";

interface Step {
  isLoading?: boolean;
  disabled?: boolean;
  valid?: boolean;
  title: string;
  description: string;
}

export interface SetupWizardState {
  isLoading: boolean;
  currentStep: number;
  steps: Step[];
}

const isValidDeclarationOfConformity = (
  declaration: DeclarationOfConformityDto
) =>
  !!declaration.name &&
  !!declaration.title &&
  !!declaration.titleFunction &&
  !!declaration.company &&
  !!declaration.country &&
  !!declaration.address &&
  !!declaration.corporateNumber &&
  !!declaration.applicationName &&
  !!declaration.version &&
  !!declaration.agree;

const stepValidators: {
  [idx: number]: (state: ApplicationState) => boolean;
} = {
  1: (state: ApplicationState) => {
    const name = state.sessionSetup.session?.name;
    const testSessionTypeId = state.sessionSetup.session?.testSessionTypeId;
    const selectedTestSessionType = state.sessionSetup.testSessionTypes?.find(
      (f) => f.id === testSessionTypeId
    );
    const declarationOfConformity =
      state.sessionSetup.session?.declarationOfConformity;

    return !!(
      name &&
      (selectedTestSessionType?.name?.toLowerCase() === "self-assessment"
        ? true
        : declarationOfConformity &&
          isValidDeclarationOfConformity(declarationOfConformity))
    );
  },
  2: (state: ApplicationState) => {
    return !!(
      state.sessionSetup.step2.functions?.some(
        (f) => f.conformityOption !== ConformityLevel.None
      ) &&
      !state.sessionSetup.step2.profiles?.some((p) =>
        hasUnmetDependencies(p)
      ) &&
      state.sessionSetup.step2.functions
        ?.filter((f) => f.conformityOption !== ConformityLevel.None)
        .every(
          (f) =>
            f.unmetImportProfileDependencies?.length === 0 &&
            f.unmetExportProfileDependencies?.length === 0 &&
            f.unmetFunctionDependencies?.length === 0
        )
    );
  },

  3: (state: ApplicationState) => {
    return (
      state.sessionSetup.session?.status?.short !== "assist" &&
      state.sessionSetup.step3.testCases?.every((tc) => allFilesUploaded(tc))
    );
  },
};

const isCurrentStepValid = (state: ApplicationState) => {
  return stepValidators[state.setupWizard.currentStep](state);
};

const initialState: SetupWizardState = {
  isLoading: false,
  currentStep: 1,
  steps: [
    {
      disabled: false,
      valid: true,
      title: "Select type",
      description: "Test on your own or with DNV's help",
      isLoading: false,
    },
    {
      disabled: false,
      valid: true,
      title: "Select tests",
      description: "Test on your own or with DNV's help",
      isLoading: false,
    },
    {
      disabled: true,
      valid: true,
      title: "Upload files",
      description: "Upload your prepared test files",
      isLoading: false,
    },
  ],
};

export const setupWizardSlice = createSlice({
  name: "setupSession",
  initialState: { ...initialState },
  reducers: {
    reset: (state) => initialState,

    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setStepIsLoading: (
      state,
      action: PayloadAction<{ stepId: number; value: boolean }>
    ) => {
      state.steps = state.steps.map((s, idx) => ({
        ...s,
        isLoading:
          idx + 1 === action.payload.stepId
            ? action.payload.value
            : s.isLoading,
      }));
    },

    updateCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },

    setStepValid: (
      state,
      action: PayloadAction<{ value: boolean; stepId: number }>
    ) => {
      state.steps[action.payload.stepId - 1] = {
        ...state.steps[action.payload.stepId - 1],
        valid: action.payload.value,
      };
    },

    lockUnlockNextSteps: (
      state,
      action: PayloadAction<{ value: boolean; stepId: number }>
    ) => {
      state.steps = state.steps.map((s, idx) => ({
        ...s,
        disabled:
          idx + 1 > action.payload.stepId ? action.payload.value : s.disabled,
      }));
    },
  },
});

export const { actions } = setupWizardSlice;

export const resetSetupWizard = () => (dispatch: any) => {
  dispatch(actions.reset());
  dispatch(checkIfCurrentStepIsValid());
};

export const setCurrentStep = (id: number) => (dispatch: any) => {
  dispatch(actions.updateCurrentStep(id));
};

export const stepChanged =
  (nextStep: { id: number }) =>
  (dispatch: any, getState: () => ApplicationState) => {
    const state = getState();
    const currentStep = state.setupWizard.currentStep;
    const session = state.sessionSetup.session;

    if (currentStep === 1 && nextStep.id === 2) {
      dispatch(actions.setStepIsLoading({ stepId: currentStep, value: true }));
      dispatch(actions.setIsLoading(true));
      updateStep1({
        sessionId: session?.id,
        sessionName: session?.name,
        testSessionTypeId: session?.testSessionTypeId,
        declarationOfConformity: session?.declarationOfConformity,
      })
        .then((a) => toast.success("Session saved"))
        .catch(() => {
          // restClient already showed why saving failed
        })
        .finally(() => {
          dispatch(actions.setIsLoading(false));
          dispatch(
            actions.setStepIsLoading({ stepId: currentStep, value: false })
          );
          dispatch(actions.updateCurrentStep(nextStep.id));
        });
    } else if (currentStep === 2 && (nextStep.id === 3 || nextStep.id === 1)) {
      dispatch(actions.setStepIsLoading({ stepId: currentStep, value: true }));
      dispatch(actions.setIsLoading(true));
      updateStep2(
        session?.id ?? "",
        state.sessionSetup.step2.profiles
          ? state.sessionSetup.step2.profiles?.map((fn) => {
              return {
                profileId: fn.id,
                profileName: fn.name,
                individualImportOption:
                  fn.importSelections?.individualOption?.selection,
                individualExportOption:
                  fn.exportSelections?.individualOption?.selection,
                inclusiveImportOption:
                  fn.importSelections?.inclusiveOption?.selection,
                inclusiveExportOption:
                  fn.exportSelections?.inclusiveOption?.selection,
                differenceImportOption:
                  fn.importSelections?.differenceOption?.selection,
                differenceExportOption:
                  fn.exportSelections?.differenceOption?.selection,
              };
            })
          : [],
        state.sessionSetup.step2.functions
          ? state.sessionSetup.step2.functions?.map((fn) => {
              return {
                functionId: fn.id,
                functionName: fn.name,
                option:
                  fn.name === "Import"
                    ? minImportLevelForProfiles(
                        state.sessionSetup.step2.profiles ?? []
                      )
                    : fn.name === "Export"
                    ? minExportLevelForProfiles(
                        state.sessionSetup.step2.profiles ?? []
                      )
                    : fn.conformityOption,
              };
            })
          : []
      )
        .then(() => toast.success("Session saved"))
        //.then(() => dispatch(getStep3Data()))
        .catch(() => {
          // restClient already showed why saving failed
        })
        .finally(() => {
          dispatch(actions.setIsLoading(false));
          dispatch(
            actions.setStepIsLoading({ stepId: currentStep, value: false })
          );
          dispatch(actions.updateCurrentStep(nextStep.id));
        });
    } else {
      dispatch(actions.updateCurrentStep(nextStep.id));
    }
  };

export const checkIfCurrentStepIsValid =
  () => (dispatch: any, getState: () => ApplicationState) => {
    const state = getState();
    const isValid = isCurrentStepValid(state);
    dispatch(
      actions.lockUnlockNextSteps({
        value: !isValid,
        stepId: state.setupWizard.currentStep,
      })
    );
    dispatch(
      actions.setStepValid({
        value: isValid,
        stepId: state.setupWizard.currentStep,
      })
    );
  };
